import { useContext, useEffect, useRef, useState } from 'react'
import HoldersList from '@/components/HoldersList'
import {
  hasLoadedPresaleHolders,
  loadPresaleHolders,
  snapshotPresaleHolders,
  subscribePresaleHoldersChanged,
  type LaunchHolder,
} from '@/lib/presale/holders'
import type { SimPresale } from '@/lib/presale/types'
import { WalletAddressContext } from '@/lib/walletContext'

function presaleKey(presale: SimPresale, wallet?: string) {
  return [presale.id, presale.contractAddress, presale.raisedWei.toString(), wallet]
    .filter(Boolean)
    .join(':')
    .toLowerCase()
}

function PresaleHolders({ presale }: { presale: SimPresale }) {
  const wallet = useContext(WalletAddressContext)
  const seed = snapshotPresaleHolders(presale)
  const [holders, setHolders] = useState<LaunchHolder[]>(seed)
  const [loading, setLoading] = useState(seed.length === 0)
  const fetchedKey = useRef('')

  useEffect(() => {
    const key = presaleKey(presale, wallet)
    let cancelled = false
    const apply = (rows: LaunchHolder[]) => {
      if (cancelled)
        return
      setHolders(rows)
      setLoading(rows.length === 0 && presale.raisedWei > 0n)
    }

    const cached = snapshotPresaleHolders(presale)
    if (hasLoadedPresaleHolders(presale) && fetchedKey.current === key && cached.length > 0) {
      apply(cached)
      return () => {
        cancelled = true
      }
    }

    fetchedKey.current = key
    setLoading(cached.length === 0)
    loadPresaleHolders(presale, { wallet })
      .then((rows) => {
        apply(rows)
        const needsRetry = rows.length === 0 && presale.raisedWei > 0n
        if (cancelled || !needsRetry)
          return
        window.setTimeout(() => {
          if (cancelled)
            return
          loadPresaleHolders(presale, { force: true, wallet }).then(apply).catch(() => {})
        }, 1200)
      })
      .catch(() => {
        if (!cancelled)
          setLoading(false)
      })

    const cacheKey = (presale.contractAddress || presale.id).toLowerCase()
    const stop = subscribePresaleHoldersChanged((changed) => {
      if (changed !== cacheKey)
        return
      loadPresaleHolders(presale, { force: true, wallet }).then(apply).catch(() => {})
    })

    return () => {
      cancelled = true
      stop()
    }
  }, [presale.id, presale.contractAddress, presale.raisedWei, wallet])

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
      <h2 className="text-sm font-semibold text-white">Holders</h2>
      <HoldersList holders={holders} loading={loading} emptyLabel="No holders yet." />
    </div>
  )
}

export default PresaleHolders
